import React, { useState, useEffect } from "react";
import { Grid, Typography, Box, Button as MuiButton } from "@mui/material";
import JobDetailComp from "../../../components/JobDetailComp";
import Button from "../../../components/Button";
import DialogComp from "../../../components/Dialog-Popup";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import "./style.css";

export default function JobDetailsFun() {
  const { jobId } = useParams();
  const [jobDetails, setJobDetails] = useState({
    title: "",
    description: "",
    jobType: "",
    jobCategory: "",
    location: "",
    emergency: false,
    recurring: false,
    scheduledTime: "",
    image_path: "",
    status: "",
    owner: {
      ratings: 0,
      reviews: ""
    }
  });
  const [jobImage, setJobImage] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupContent, setPopupContent] = useState("");
  const [buttonArr, setButtonArr] = useState([]);
  
  const navigate = useNavigate();

  // Fetch job details when the component mounts
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }
        
        const response = await axios.get(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        console.log("Fetched job details:", response.data);
        setJobDetails(response.data);
        
        // Try to fetch the job image after getting details
        fetchJobImage(jobId, token);
      } catch (err) {
        console.error("Error fetching job details:", err);
        setError("Failed to fetch job details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobDetails();
  }, [jobId, navigate]);

  // Function to fetch job image
  const fetchJobImage = async (id, token) => {
    try {
      const response = await axios.get(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}/image`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob' // Important for image data
      });
      
      if (response.data) {
        // Create a URL for the blob data
        const imageUrl = URL.createObjectURL(response.data);
        setJobImage(imageUrl);
      }
    } catch (err) {
      console.error("Error fetching job image:", err);
      // Try the direct path approach as fallback
      if (jobDetails.image_path) {
        setJobImage(formatImagePath(jobDetails.image_path));
      }
    }
  };

  // Format the scheduledTime to display in a readable format
  const formatDate = (dateTime) => {
    if (!dateTime) return "Not scheduled";
    return moment(dateTime).format("D MMM YYYY");
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "";
    return moment(dateTime).format("LT");
  };

  // Format image path for direct access
  const formatImagePath = (path) => {
    if (!path) return '';
    
    // Remove 'src' prefix if it exists and replace backslashes with forward slashes
    const formattedPath = path.replace(/\\/g, '/').replace(/^src\//, '');
    
    // Create a URL to the static resource
    return `https://jobmanagement-497524552274.us-central1.run.app/uploads/${formattedPath.split('/').pop()}`;
  };

  // Event handler for the "View Image" button
  const handleViewImage = () => {
    if (jobImage) {
      setShowFullImage(!showFullImage);
    } else if (jobDetails.image_path) {
      setJobImage(formatImagePath(jobDetails.image_path));
    }
  };

  // Dialog popup handling
  const closeFunc = () => setOpenPopup(false);

  const cancelJobPopupFun = () => {
    console.log("Cancel Job Popup triggered");
    setOpenPopup(true);
    setPopupTitle("Cancel Job");
    setPopupContent("You are trying to cancel the Service request. Please confirm.");
    setButtonArr([
      {
        id: "1",
        value: "Confirm",
        fontWeight: "bold",
        color: "borderLess",
        functionCall: async () => {
          try {
            const token = localStorage.getItem("token");
            if (!token) {
              navigate("/login");
              return;
            }
            await axios.put(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/cancel`, null, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setOpenPopup(false);
            setPopupTitle("Success");
            setPopupContent("Job has been successfully canceled.");
            setButtonArr([{
              id: "1",
              value: "OK",
              fontWeight: "bold",
              color: "borderLess",
              functionCall: () => navigate(`/CancelledAndRescheduledJobs/${jobId}`),
            }]);
            setOpenPopup(true);
          } catch (err) {
            console.error("Error canceling the job:", err);
            setPopupContent("Failed to cancel the job. Please try again.");
          }
        },
      }, 
      {
        id: "2",
        value: "Cancel",
        fontWeight: "bold",
        color: "borderLess",
        functionCall: closeFunc,
      }
    ]);
  };

  if (loading) {
    return <Typography>Loading job details...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Grid container>
      <Grid container padding="3%">
        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            Job Details
          </Typography>
          <Typography variant="h4" sx={{ color: "text.secondary" }}>
            - {jobId}
          </Typography>
        </Grid>
        
        <JobDetailComp showPlumberCard={false} />
        
        {/* Basic Job Details Section */}
        <Grid item xs={12} sx={{ p: 2, mt: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Basic Information
          </Typography>
          
          <div className="labelDivCls">
            <div className="labelCls">Job Title</div>
            <div className="valueCls">{jobDetails.title || "N/A"}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Job Type</div>
            <div className="valueCls">{jobDetails.jobType || "N/A"}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Job Category</div>
            <div className="valueCls">{jobDetails.jobCategory || "N/A"}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Description</div>
            <div className="valueCls">{jobDetails.description || "N/A"}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Location</div>
            <div className="valueCls">{jobDetails.location || "N/A"}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Status</div>
            <div className="valueCls">{jobDetails.status || "Pending"}</div>
          </div>
        </Grid>

        {/* Emergency and Scheduling Details Section */}
        <Grid item xs={12} sx={{ p: 2, mt: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Schedule & Priority
          </Typography>
          
          <div className="labelDivCls">
            <div className="labelCls">Is this an emergency?</div>
            <div className="valueCls">{jobDetails.emergency ? "Yes" : "No"}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Job Date</div>
            <div className="valueCls">{formatDate(jobDetails.scheduledTime)}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Selected Time</div>
            <div className="valueCls">{formatTime(jobDetails.scheduledTime)}</div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Is this a recurring issue?</div>
            <div className="valueCls">{jobDetails.recurring ? "Yes" : "No"}</div>
          </div>
        </Grid>

        {/* Job Image Section */}
        <Grid item xs={12} sx={{ p: 2, mt: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Job Images
          </Typography>
          
          {jobImage ? (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img 
                src={jobImage} 
                alt="Job Image" 
                style={{ 
                  maxWidth: showFullImage ? '100%' : '300px', 
                  maxHeight: showFullImage ? 'auto' : '200px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setShowFullImage(!showFullImage)}
              />
              {showFullImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Click the image to resize
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>No image available for this job</Typography>
          )}
          
          <MuiButton
            variant="outlined"
            color="primary"
            onClick={handleViewImage}
            sx={{ mt: 2 }}
            disabled={!jobImage && !jobDetails.image_path}
          >
            {showFullImage ? "Resize Image" : "View Full Image"}
          </MuiButton>
        </Grid>

        {/* Additional Actions */}
        <Grid item xs={12} sx={{ p: 2, mt: 2 }}>
          
          
          
        </Grid>
      </Grid>
      
      {/* Dialog Component */}
      <DialogComp
        key={openPopup} // Force re-render when openPopup changes
        openPopup={openPopup}
        closeFunc={closeFunc}
        popuptitle={popupTitle}
        popupContent={popupContent}
        buttonValue={buttonArr}
      />
      
      {/* Sticky Bottom Action Buttons */}
      <Grid
        display="flex"
        justifyContent="space-between"
        bgcolor="#ffffff"
        padding="12px 20px"
        width="100%"
        style={{ position: "sticky", bottom: "0" }}
      >
        <Button name="Cancel Job" onClick={cancelJobPopupFun} />
        <Button name="Reschedule Job" color="green" onClick={() => navigate(`/ReschedulePageFun/${jobId}`)} />
      </Grid>
    </Grid>
  );
}